'use client'

import {
  IInquiryDocumentUpload,
  ShipmentCustomsType,
  ShipmentDocumentType,
  useDocumentApi,
} from '@repo/sdk'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'

import { Context, ProviderProps } from './types'

const DropzoneContext = createContext<Context>({} as Context)

export function DropzoneContextProvider({ children, ...props }: ProviderProps) {
  const [documents, setDocuments] = useState<IInquiryDocumentUpload[]>([])
  const { updateDocumentType, uploadUnassignedDocument } = useDocumentApi()

  const addDocuments = (files: File[]) => {
    const newFiles = files.map(
      file =>
        ({
          file,
          status: 'pending',
          type: ShipmentDocumentType.other,
        }) satisfies IInquiryDocumentUpload,
    )

    setDocuments(prev => [...prev, ...newFiles])
  }

  const uploadDocuments = useCallback(async () => {
    const pendingDocuments = documents.filter(
      doc => doc.status === 'pending' && doc.id === undefined,
    )

    if (pendingDocuments.length === 0) return

    for (const doc of pendingDocuments) {
      try {
        const uploadedDoc = (await uploadUnassignedDocument(doc))[0]

        if (!uploadedDoc || uploadedDoc.id === undefined) {
          await updateDocument(doc, {
            error: { message: 'Upload failed or no ID returned' },
            status: 'error',
          })

          continue
        }

        await updateDocument(doc, {
          id: uploadedDoc.id,
          status: 'done',
        })
      } catch (error) {
        await updateDocument(doc, {
          error: { message: String(error) },
          status: 'error',
        })
      }
    }
  }, [documents, uploadUnassignedDocument, setDocuments])

  const removeDocument = (document: IInquiryDocumentUpload) => {
    setDocuments(prev => prev.filter(doc => document.file !== doc.file))
  }

  const updateDocument = async (
    document: IInquiryDocumentUpload,
    data: Partial<IInquiryDocumentUpload>,
  ) => {
    if ('async' in props && 'type' in data) {
      if (!document.id) return

      let updatedData: Partial<IInquiryDocumentUpload> =
        'async' in props ? { ...data, status: 'pending' } : data

      setDocuments(prev =>
        prev.map(doc => (document.file === doc.file ? { ...doc, ...updatedData } : doc)),
      )

      await updateDocumentType(document.id, data.type)

      setDocuments(prev =>
        prev.map(doc =>
          document.file === doc.file ? { ...doc, ...updatedData, status: 'done' } : doc,
        ),
      )
    } else {
      setDocuments(prev =>
        prev.map(doc => (document.file === doc.file ? { ...doc, ...data } : doc)),
      )
    }
  }

  const documentTypes = [
    { label: 'Commercial invoice', type: ShipmentCustomsType.commercialInvoice },
    {
      label: 'Export / import document',
      type: ShipmentCustomsType.exportImportDocumentation,
    },
    { label: 'Packing list', type: ShipmentCustomsType.packingList },
    { label: 'Other custom type', type: ShipmentCustomsType.other },
    { label: 'Custom', type: ShipmentDocumentType.custom },
    { label: 'Invoice', type: ShipmentDocumentType.invoice },
    { label: 'Proof of delivery (POD)', type: ShipmentDocumentType.pod },
    { label: 'Other', type: ShipmentDocumentType.other },
  ]

  useEffect(() => {
    if ('async' in props) {
      uploadDocuments()
    }
  }, [documents])

  return (
    <DropzoneContext.Provider
      value={{
        ...props,
        addDocuments,
        documents,
        documentTypes,
        removeDocument,
        setDocuments,
        updateDocument,
      }}
    >
      {children}
    </DropzoneContext.Provider>
  )
}

export const useDropzoneContext = () => {
  const context = useContext(DropzoneContext)

  if (!context) {
    throw new Error('useDropzoneContext must be used within a DropzoneContextProvider')
  }

  return context
}
